import { closestCorners, DndContext, DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { MyStoreBundle } from '@1po/1po-bff-fe-spec/generated/my_store/bundles/response/GetMyStoreBundlesResponse';
import { MyStoreReference } from '@1po/1po-bff-fe-spec/generated/my_store/references/response/GetMyStoreReferencesResponse';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { ColumnsType } from 'antd/es/table';
import { ROUTER_ESTIMATE } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { trackAppEvent } from 'app/AppTracker';
import { AddToEstimateButtonAndDialog } from 'components/AddToEstimate/AddToEstimateButtonAndDialog';
import { DataContainer } from 'components/DataContainer';
import { addReferenceFromAutocomplete } from 'domains/estimate/Estimate.store';
import { useFetchMyStoreReferences } from 'domains/myStore/MyStore.requests';
import {
  addReferenceRequestSaga,
  addReferencesSectionRequestSaga,
  createOrUpdateReferenceSaga,
  getAllReferenceNumbers,
  getAllSelectedReferences,
  getIsReferenceSelected,
  getIsReferencesSectionSelected,
  getReferencesSections,
  moveReferencesSectionRequestSaga,
  removeReferenceRequestSaga,
  removeReferenceSectionRequestSaga,
  renameReferencesSectionRequestSaga,
  setReferenceSelection,
} from 'domains/myStore/MyStore.store';
import { REFERENCES } from 'domains/myStore/MyStore.types';
import { GetTitle } from 'pages/EstimatePage/TableSection/tableComponents';
import MyStoreHeader from 'pages/MyStorePage/MyStoreHeader';
import BundlesHeader from 'pages/MyStorePage/Pages/Bundles/BundlesHeader';
import BundlesSection, { SelectBundles } from 'pages/MyStorePage/Pages/Bundles/BundlesSection';
import { ValidatedPriceInput, ValidatedTextInput } from 'pages/MyStorePage/Pages/Bundles/BundlesTable/BundlesInput';
import { CenteredSpin, CenterFlex, Container, Flex, MarginBox, NotificationLink, notifyTop, TrashAltButton } from 'UI';
import { restrictToContainerId } from 'utils/dnd-utils';
import { TRACKING_EVENT_ADD_TO_ESTIMATE, TRACKING_EVENT_GO_TO_ESTIMATE } from 'utils/eventTracker/EventTracker.types';
import { scrollToTopSmooth, useResetScroll } from 'utils/hooks/useResetScroll';

export const References = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const referencesSections = useSelector(getReferencesSections);
  const allReferenceNumbers = useSelector(getAllReferenceNumbers);
  const selectedReferences = useSelector((state: RootState) => getAllSelectedReferences(state));
  const [isDraggingSectionId, setIsDraggingSectionId] = React.useState<string>('');

  useFetchMyStoreReferences();
  useResetScroll();

  const handleDragStart = (event: DragStartEvent) => {
    setIsDraggingSectionId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setIsDraggingSectionId('');
    dispatch(moveReferencesSectionRequestSaga(event));
  };

  const handleAddReference = (sectionId: string) => {
    dispatch(addReferenceRequestSaga({ sectionId }));
  };

  const handleAddSection = () => {
    dispatch(addReferencesSectionRequestSaga());
    scrollToTopSmooth();
  };

  const handleDeleteReferenceSection = (sectionId: string) => {
    dispatch(removeReferenceSectionRequestSaga({ sectionId }));
  };

  const handleOnChangeSelection = (sectionId: string, isSelected: boolean, referenceId?: string) => {
    dispatch(setReferenceSelection({ sectionId, isSelected, referenceId }));
  };

  const handleSectionTitleChange = (sectionId: string, title: string) => {
    dispatch(renameReferencesSectionRequestSaga({ sectionId, title }));
  };

  const handleAddToEstimateClick = (references: MyStoreReference[], _quantity?: number) => {
    notifyTop(
      'success',
      references.length > 1 ? (
        <Trans i18nKey={'estimate.references.addedMultipleToEstimate'}>
          {'References have been added to your estimate.'}
        </Trans>
      ) : (
        <Trans i18nKey={'estimate.references.addedToEstimate'}>{'Reference has been added to your estimate.'}</Trans>
      ),
      undefined,
      <NotificationLink
        onClick={() => {
          trackAppEvent(TRACKING_EVENT_GO_TO_ESTIMATE);
          history.push(`${ROUTER_ESTIMATE}`);
        }}
      >
        <Trans i18nKey={'catalog.reference_card.added_to_estimate.go_to_estimate'}>{'Go to estimate'}</Trans>
      </NotificationLink>,
    );
    trackAppEvent(TRACKING_EVENT_ADD_TO_ESTIMATE);
    references.forEach((reference) => {
      dispatch(addReferenceFromAutocomplete({ reference, itemId: reference.referenceId }));
    });
  };

  const isReferenceNumberUnique = (newVal: string, oldVal: string) => {
    if (newVal === oldVal) {
      return true;
    }
    return !allReferenceNumbers.includes(newVal);
  };

  function isReferenceRowValid(row: MyStoreReference): boolean {
    return (
      !!row &&
      !!row?.referenceNumber &&
      !!row?.designation &&
      !!row?.clientPriceVatExcluded &&
      !!row?.garagePriceVatExcluded
    );
  }

  const getColumns = (sectionId: string) => {
    return [
      {
        title: (
          <CenterFlex>
            <SelectBundles
              entityId={sectionId}
              enabled={true}
              handleOnChange={(isSelected: boolean) => handleOnChangeSelection(sectionId, isSelected)}
              getIsSelectedSelector={getIsReferencesSectionSelected}
            />
          </CenterFlex>
        ),
        dataIndex: 'referenceId',
        render: (referenceId: string, row: MyStoreReference) => (
          <CenterFlex>
            <SelectBundles
              entityId={referenceId}
              enabled={isReferenceRowValid(row)}
              handleOnChange={(isSelected: boolean) => handleOnChangeSelection(sectionId, isSelected, referenceId)}
              getIsSelectedSelector={getIsReferenceSelected}
            />
          </CenterFlex>
        ),
        width: 48,
      },
      {
        title: GetTitle(t('estimate.references.reference_number', 'Reference number')),
        dataIndex: 'referenceNumber',
        render: (refNumb: string, row: MyStoreReference) => (
          <ValidatedTextInput
            value={refNumb}
            validate={(v) => v.length > 0 && isReferenceNumberUnique(v, refNumb)}
            saveFunction={(referenceNumber) => {
              if (referenceNumber !== refNumb) {
                dispatch(
                  createOrUpdateReferenceSaga({
                    sectionId,
                    referenceId: row.referenceId,
                    fieldUpdate: { referenceNumber },
                  }),
                );
              }
            }}
          />
        ),
        width: 300,
      },
      {
        title: GetTitle(t('common.designation', 'Designation')),
        dataIndex: 'designation',
        render: (designation: string, row: MyStoreReference) => (
          <ValidatedTextInput
            value={designation}
            validate={(v) => v.length > 0}
            saveFunction={(referenceDesignation) => {
              if (referenceDesignation !== designation) {
                dispatch(
                  createOrUpdateReferenceSaga({
                    sectionId,
                    referenceId: row.referenceId,
                    fieldUpdate: { designation: referenceDesignation },
                  }),
                );
              }
            }}
          />
        ),
      },
      {
        title: GetTitle(t('common.price.VAT_excl_price', 'VAT Excl. Price')),
        dataIndex: 'clientPriceVatExcluded',
        render: (clientPriceVatExcluded: string, row: MyStoreReference) => (
          <ValidatedPriceInput
            value={clientPriceVatExcluded}
            validate={(v) => v !== undefined && v != null && v?.toString()?.length > 0}
            saveFunction={(referenceClientPrice) => {
              if (referenceClientPrice && referenceClientPrice !== clientPriceVatExcluded) {
                dispatch(
                  createOrUpdateReferenceSaga({
                    sectionId,
                    referenceId: row.referenceId,
                    fieldUpdate: { clientPriceVatExcluded: referenceClientPrice },
                  }),
                );
              }
            }}
          />
        ),
        width: 170,
      },
      {
        title: GetTitle(t('common.price.garage_price', 'Garage Price')),
        dataIndex: 'garagePriceVatExcluded',
        render: (garagePriceVatExcluded: string, row: MyStoreReference) => (
          <ValidatedPriceInput
            value={garagePriceVatExcluded}
            validate={(v) => v !== undefined && v != null && v?.toString()?.length > 0}
            saveFunction={(referencePrice) => {
              if (referencePrice && referencePrice !== garagePriceVatExcluded) {
                dispatch(
                  createOrUpdateReferenceSaga({
                    sectionId,
                    referenceId: row.referenceId,
                    fieldUpdate: { garagePriceVatExcluded: referencePrice },
                  }),
                );
              }
            }}
          />
        ),
        width: 170,
      },
      {
        title: '',
        dataIndex: 'referenceId',
        render: (rowId: string) => (
          <CenterFlex>
            <TrashAltButton
              onClick={() => {
                dispatch(removeReferenceRequestSaga({ sectionId, rowId }));
              }}
            />
          </CenterFlex>
        ),
        width: 48,
      },
      {
        title: '',
        render: (row: MyStoreReference) => (
          <CenterFlex>
            <AddToEstimateButtonAndDialog
              buttonComponent={'icon'}
              disabled={!isReferenceRowValid(row)}
              handleAddToEstimateClick={(quantity) => handleAddToEstimateClick([row], quantity)}
            />
          </CenterFlex>
        ),
        width: 48,
      },
    ];
  };

  return (
    <Flex direction={'column'}>
      <DataContainer
        data={referencesSections.sections.searchStatus}
        Loading={() => (
          <MarginBox mt={'10%'}>
            <CenteredSpin />
          </MarginBox>
        )}
      >
        <MyStoreHeader
          rightSection={
            <BundlesHeader
              myStoreType={REFERENCES}
              disabled={!selectedReferences?.length}
              handleAddSection={handleAddSection}
              handleAddToEstimateClick={(quantity) => handleAddToEstimateClick(selectedReferences, quantity)}
            />
          }
        />
        <Container>
          <div id={'dnd-container'}>
            <Flex direction={'column'} gap={15}>
              <DndContext
                collisionDetection={closestCorners}
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
                modifiers={[restrictToVerticalAxis, restrictToContainerId('dnd-container')]}
              >
                <SortableContext
                  items={(referencesSections.sections.data ?? []).map((e) => ({ id: e.sectionId, ...e }))}
                  strategy={verticalListSortingStrategy}
                >
                  {referencesSections.sections.data?.map((referencesSection, index) => (
                    <BundlesSection
                      key={referencesSection.sectionId}
                      sectionId={referencesSection.sectionId}
                      title={referencesSection?.title ?? ''}
                      items={referencesSection.references ?? []}
                      isDragging={referencesSection.sectionId === isDraggingSectionId}
                      initiallyOpen={index === 0}
                      myStoreType={REFERENCES}
                      handleAddItemInSection={() => handleAddReference(referencesSection.sectionId)}
                      handleDeleteSection={() => handleDeleteReferenceSection(referencesSection.sectionId)}
                      handleOnChangeSelection={handleOnChangeSelection}
                      handleSectionTitleChange={handleSectionTitleChange}
                      getIsSelectedSelector={getIsReferencesSectionSelected}
                      columns={getColumns(referencesSection.sectionId) as ColumnsType<MyStoreBundle | MyStoreReference>}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </Flex>
          </div>
        </Container>
      </DataContainer>
    </Flex>
  );
};
